import environment from '@/environment';
import { firebaseAuth, logout } from '../auth/auth.api';
import logger from '@/services/logger';
import type { ApiResponse } from './types/api-response';
import { ApiRequestError } from './types/api-request-error';
import { useAuthState } from '@/state/user';
import { sanitizeNamePart } from '@/utils/display-name';
import { sanitizeEndpoint } from '@/services/telemetry-sanitizer';
import { RouteName } from '@/router/types';
import { getPendingRegistration } from '../auth/pending-registration';

const BASE_URL = environment.apiBaseUrl;

type FirebaseUser = NonNullable<typeof firebaseAuth.currentUser>;

const { setBackendUserProvisioned } = useAuthState();

/** Exact backend message for a valid Firebase token with no GYMing document. */
export const MISSING_BACKEND_USER_MESSAGE = 'User not found';

const PROVISIONING_ENDPOINT = 'auth/register';

/** Repairing a failed register with another register would be pure recursion. */
const isProvisioningEndpoint = (endpoint: string) => endpoint.split('?')[0] === PROVISIONING_ENDPOINT;

/**
 * In-flight or settled self-heal per uid. Concurrent "User not found" responses
 * must share one attempt: telling the losers "already attempted" logged the user
 * out while the repair they depended on was still on the wire. Entries are never
 * removed, so a uid still gets at most one attempt and a backend that keeps
 * answering "User not found" falls through to logout instead of retrying forever.
 */
const provisioningByUid = new Map<string, Promise<ProvisioningResult>>();

/** Latched once the teardown is committed, so only one caller navigates. */
let logoutStarted = false;

/** Test seam: a document navigation resets both in the real app. */
export function resetAuthRecoveryState() {
  provisioningByUid.clear();
  logoutStarted = false;
  registrationRedirectStarted = false;
}

async function readResponseMessage(res: Response): Promise<string | null> {
  try {
    const source = typeof res.clone === 'function' ? res.clone() : res;
    const body = (await source.json()) as ApiResponse<unknown> | null;
    return body?.message ?? null;
  } catch {
    return null;
  }
}

/** `registration-required`: the backend needs a date of birth first (strict age enforcement). */
type ProvisioningResult = 'provisioned' | 'failed' | 'registration-required';

/** The date-of-birth step; reached by a full navigation, at most once, never from itself. */
let registrationRedirectStarted = false;
function goToCompleteRegistration() {
  if (registrationRedirectStarted || window.location.pathname === `/${RouteName.CompleteRegistration}`) return;
  registrationRedirectStarted = true;
  window.location.assign(`/${RouteName.CompleteRegistration}`);
}

/**
 * Repairs an account that exists in Firebase but never got a GYMing document,
 * which the pre-2.27 signup flow could leave behind. Names are deliberately
 * passed through untouched: the backend owns the fallback when they are empty,
 * so a recovered orphan is never given an invented profile name. With strict age
 * enforcement the backend refuses this without a date of birth: the user is then
 * sent to the date-of-birth step instead of being logged out.
 */
async function requestProvisioning(currentUser: FirebaseUser): Promise<ProvisioningResult> {
  const [first, last] = (currentUser.displayName ?? '').split(' ');
  try {
    await sendRequest<ApiResponse<unknown>>(
      PROVISIONING_ENDPOINT,
      'POST',
      {
        firstName: sanitizeNamePart(first),
        lastName: sanitizeNamePart(last),
        avatarUrl: currentUser.photoURL || null,
      },
      undefined,
      { provisioning: 'request' }
    );
    setBackendUserProvisioned(true);
    logger.info('Provisioned missing backend user for Firebase account', { uid: currentUser.uid });
    return 'provisioned';
  } catch (error) {
    if (error instanceof ApiRequestError && error.code === 'DATE_OF_BIRTH_REQUIRED') {
      return 'registration-required';
    }
    logger.error({
      message: 'Failed to provision missing backend user',
      data: { uid: currentUser.uid },
      error: error as Error,
    });
    return 'failed';
  }
}

/**
 * Registers the attempt synchronously, before the first await, so requests that
 * 401 in the same tick join it instead of each starting their own.
 */
function provisionMissingBackendUser(): Promise<ProvisioningResult> {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) return Promise.resolve('failed');
  // A new Google / Apple identity whose date-of-birth step is open (or opening), or the step
  // itself: never register without the date of birth, whatever the backend mode.
  if (
    getPendingRegistration()?.uid === currentUser.uid ||
    window.location.pathname === `/${RouteName.CompleteRegistration}`
  ) {
    return Promise.resolve('registration-required');
  }

  const existing = provisioningByUid.get(currentUser.uid);
  if (existing) return existing;

  const attempt = requestProvisioning(currentUser);
  provisioningByUid.set(currentUser.uid, attempt);
  return attempt;
}

/**
 * Four simultaneous 401s once ran four logouts and four document navigations,
 * which left iOS with an unloaded document and a blank screen. The navigation
 * happens even if sign-out throws, so the latch can never wedge the app.
 */
async function logoutAndReturnToLogin(method: HttpMethod, endpoint: string) {
  if (logoutStarted) return;
  logoutStarted = true;
  // Never serialize the Firebase user: its toJSON() carries the access and refresh tokens.
  logger.info('401 error, logging out', {
    method,
    endpoint: sanitizeEndpoint(endpoint),
    uid: firebaseAuth.currentUser?.uid ?? null,
  });
  try {
    await logout();
  } finally {
    window.location.assign('/login');
  }
}

/** Default for regular JSON requests. Uploads (postFormData) have no default timeout. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Thrown when a request exceeds its timeout. Named `TimeoutError` (not `AbortError`)
 * so callers that silently ignore their own cancellations still see timeouts.
 */
export class RequestTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`Request timed out after ${timeoutMs} ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Combines the caller's signal with a timeout. Uses a plain AbortController and timer
 * rather than AbortSignal.any/timeout, which older iOS WebViews lack. `dispose` must
 * run once the request (including the body read) settles: it clears the timer and
 * removes the listener from the caller's signal, which can outlive many requests.
 */
function createRequestSignal(callerSignal: AbortSignal | undefined, timeoutMs: number) {
  if (!(timeoutMs > 0)) {
    return { signal: callerSignal, timedOut: () => false, dispose: () => undefined };
  }
  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) {
    controller.abort(callerSignal.reason);
  } else {
    callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  }
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort(new RequestTimeoutError(timeoutMs));
  }, timeoutMs);
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    dispose: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

export const httpService = {
  get<T>(endpoint: string, params?: Record<string, string>, options?: RequestOptions) {
    return sendRequest<T>(endpoint, 'GET', null, params, options);
  },
  post<T>(endpoint: string, payload?: unknown, options?: RequestOptions) {
    return sendRequest<T>(endpoint, 'POST', payload, undefined, options);
  },
  put<T>(endpoint: string, payload?: unknown, options?: RequestOptions) {
    return sendRequest<T>(endpoint, 'PUT', payload, undefined, options);
  },
  delete<T>(endpoint: string, options?: RequestOptions) {
    return sendRequest<T>(endpoint, 'DELETE', undefined, undefined, options);
  },
  postFormData<T>(endpoint: string, payload: FormData, options?: RequestOptions) {
    return sendFormData<T>(endpoint, payload, options);
  },
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

type RequestOptions = {
  signal?: AbortSignal;
  /**
   * Milliseconds before the request is aborted with a RequestTimeoutError; 0 disables
   * it. Defaults to DEFAULT_REQUEST_TIMEOUT_MS for JSON requests and to no timeout
   * for postFormData uploads, which can legitimately be slow on mobile networks.
   */
  timeoutMs?: number;
  /**
   * Internal recursion control for the self-heal, unset for normal traffic.
   * `request` is the provisioning call itself: it must neither self-heal nor
   * log out, because its caller decides what a failure means. `retry` is the
   * replayed original: no second self-heal, but a 401 is now genuine.
   */
  provisioning?: 'request' | 'retry';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function sendRequest<T = any>(
  endpoint: string,
  method: HttpMethod,
  payload?: unknown,
  params?: Record<string, string>,
  options?: RequestOptions
): Promise<Omit<Response, 'json'> & { data: T }> {
  const currentUser = firebaseAuth.currentUser;
  let authHeader = '';
  if (currentUser) {
    const token = await currentUser.getIdToken();
    authHeader = `Bearer ${token}`;
  }
  // Covers the fetch and the body read; the self-heal retry below gets its own.
  const timeoutMs = options?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const request = createRequestSignal(options?.signal, timeoutMs);
  try {
    const res = await fetch(`${BASE_URL}/api/${endpoint}?${new URLSearchParams(params)}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        authorization: authHeader,
      },
      body: payload ? JSON.stringify(payload) : undefined,
      signal: request.signal,
    });

    if (res.status === 401) {
      // "User not found" means the Firebase session is valid but the GYMing
      // document is missing. That is repairable, so provision once and retry
      // rather than logging the user out of a perfectly good session.
      const canProvision = !options?.provisioning && !isProvisioningEndpoint(endpoint);
      if (canProvision && (await readResponseMessage(res)) === MISSING_BACKEND_USER_MESSAGE) {
        const provisioned = await provisionMissingBackendUser();
        if (provisioned === 'provisioned') {
          return sendRequest<T>(endpoint, method, payload, params, { ...options, provisioning: 'retry' });
        }
        if (provisioned === 'registration-required') {
          // A pending sign-in is routed there by the app already: a full reload would lose it.
          if (!getPendingRegistration()) goToCompleteRegistration();
          throw new ApiRequestError('Registration required', 401, 'REGISTRATION_REQUIRED');
        }
      }
      if (options?.provisioning !== 'request') {
        await logoutAndReturnToLogin(method, endpoint);
      }
    }

    const data = (await res.json()) as T;
    if ((data as ApiResponse<T>)?.success === false) {
      throw new ApiRequestError(
        (data as ApiResponse<T>)?.message || 'Request failed',
        res.status,
        (data as ApiResponse<T>)?.code
      );
    }
    return {
      ...res,
      data,
    };
  } catch (caught) {
    // Our timer aborts with AbortError on WebViews that ignore abort reasons, so map
    // it back; a caller's own cancellation is rethrown unchanged.
    const error = request.timedOut() ? new RequestTimeoutError(timeoutMs) : caught;
    // Method, path and status only: request bodies and query values (search
    // terms) are personal data and stay out of telemetry.
    logger.error({
      message: 'Error on request',
      data: {
        method,
        endpoint: sanitizeEndpoint(endpoint),
        statusCode: error instanceof ApiRequestError ? error.status : null,
        errorName: error instanceof Error ? error.name : typeof error,
      },
      error: error as Error,
    });
    throw error;
  } finally {
    request.dispose();
  }
}
async function sendFormData<T = File>(
  endpoint: string,
  payload: FormData,
  options?: RequestOptions
): Promise<Omit<Response, 'json'> & { data: T }> {
  const url = `${BASE_URL}/api/${endpoint}`;
  const currentUser = firebaseAuth.currentUser;
  let authHeader = '';
  if (currentUser) {
    const token = await currentUser.getIdToken();
    authHeader = `Bearer ${token}`;
  }

  // Uploads have no default timeout (large media on slow networks); callers can opt in.
  const timeoutMs = options?.timeoutMs ?? 0;
  const request = createRequestSignal(options?.signal, timeoutMs);
  const asTimeout = (caught: unknown) => (request.timedOut() ? new RequestTimeoutError(timeoutMs) : caught);

  let res: Response;
  let text: string;
  try {
    try {
      res = await fetch(url, {
        method: 'post',
        credentials: 'include',
        headers: {
          authorization: authHeader,
        },
        body: payload,
        signal: request.signal,
      });
    } catch (caught) {
      const error = asTimeout(caught);
      logger.error({
        message: 'Network error on form data request',
        data: { method: 'POST', endpoint },
        error: error as Error,
      });
      throw error;
    }

    if (res.status === 401) {
      // Never serialize the Firebase user: its toJSON() carries the access and refresh tokens.
      logger.info('401 error on form data request, logging out', {
        method: 'POST',
        endpoint,
        uid: firebaseAuth.currentUser?.uid ?? null,
      });
      await logout();
      throw new Error(`Unauthorized: session expired (POST ${url})`);
    }

    try {
      text = await res.text();
    } catch (caught) {
      const error = asTimeout(caught);
      logger.error({
        message: 'Failed to read response body on form data request',
        data: { method: 'POST', endpoint, statusCode: res.status },
        error: error as Error,
      });
      throw error;
    }
  } finally {
    request.dispose();
  }

  if (!text.trim()) {
    const emptyMsg = `Empty response body (HTTP ${res.status}) from POST ${url}`;
    logger.error({ message: emptyMsg, data: { method: 'POST', endpoint, statusCode: res.status } });
    throw new Error(emptyMsg);
  }

  // Response bodies and raw backend messages can echo user content, so only the
  // status is logged; the thrown error keeps the message for the UI.
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const parseMsg = `Non-JSON response (HTTP ${res.status}) from POST ${url}`;
    logger.error({
      message: 'Failed to parse JSON on form data request',
      data: { method: 'POST', endpoint, statusCode: res.status },
      error: error as Error,
    });
    throw new Error(parseMsg);
  }

  const envelope = parsed as ApiResponse<unknown>;

  if (!res.ok) {
    const httpMsg = envelope?.message ?? `HTTP ${res.status} ${res.statusText}`;
    logger.error({
      message: 'HTTP error on form data request',
      data: { method: 'POST', endpoint, statusCode: res.status },
    });
    throw new Error(httpMsg);
  }

  if (envelope?.success === false) {
    const backendMsg = envelope?.message ?? 'Request failed';
    logger.error({
      message: 'Backend error on form data request',
      data: { method: 'POST', endpoint, statusCode: res.status },
    });
    throw new Error(backendMsg);
  }

  return {
    ...res,
    data: parsed as T,
  };
}
